import React, { useState, useEffect, useCallback, useMemo } from "react";
import * as XLSX from "xlsx";
import { customFetch } from '../../apiClient';

const GSTR1Report = () => {
  const [reportType, setReportType] = useState("B2B");
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().split("T")[0];
  });
  const [toDate, setToDate] = useState(
    () => new Date().toISOString().split("T")[0]
  );
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState("");

  const reportTabs = [
    { id: "B2B",        label: "B2B Invoices",          icon: "bi-building",           color: "#3b82f6" },
    { id: "B2C",        label: "B2CS (Unregistered)",    icon: "bi-people",             color: "#10b981" },
    { id: "HSN",        label: "HSN Summary",            icon: "bi-tags",               color: "#f59e0b" },
    { id: "CDNR",       label: "Credit Notes (Reg.)",    icon: "bi-arrow-return-left",  color: "#ef4444" },
    { id: "B2CS_RETURN",label: "Returns (Unreg.)",       icon: "bi-arrow-counterclockwise", color: "#8b5cf6" },
  ];

  useEffect(() => {
    setReportData([]);
    setError("");
  }, [reportType]);

  const fetchReport = async () => {
    setLoading(true);
    setError("");
    setReportData([]);
    try {
      const params = new URLSearchParams({ reportType, fromDate, toDate });
      const res = await customFetch(`/GSTR1Report/generate?${params}`);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Failed to fetch");
      }
      const data = await res.json();
      setReportData(data.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ✅ FIX: B2B/CDNR mein Invoice_Value double count avoid karo
  const summary = useMemo(() => {
    let taxable = 0, cgst = 0, sgst = 0, igst = 0, totalValue = 0;
    const uniqueBills = new Set();

    reportData.forEach(row => {
      taxable += Number(row.Taxable_Value            || 0);
      cgst    += Number(row.Central_Tax_Amount       || 0);
      sgst    += Number(row.State_UT_Tax_Amount      || 0);
      igst    += Number(row.Integrated_Tax_Amount    || 0);

      // B2B / CDNR: ek bill multiple rows mein hoga (multiple GST slabs)
      // Invoice_Value / Note_Value sirf ek baar count karo
      const billKey = row.Invoice_Number || row.Note_Number || null;
      if (billKey) {
        if (!uniqueBills.has(billKey)) {
          uniqueBills.add(billKey);
          totalValue += Number(row.Invoice_Value || row.Note_Value || 0);
        }
      } else {
        // B2C / HSN / B2CS_RETURN: consolidated rows hain — seedha add karo
        totalValue += Number(
          row.Total_Invoice_Value || row.Total_Value || row.Total_Note_Value || 0
        );
      }
    });

    return {
      taxable, cgst, sgst, igst, totalValue,
      recordCount: reportData.length,
      billCount:   uniqueBills.size || reportData.length,
    };
  }, [reportData]);

  // ✅ Export — column order maintain karo
  const exportToExcel = useCallback(() => {
    if (!reportData.length) { alert("No data to export!"); return; }
    const ws = XLSX.utils.json_to_sheet(reportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, reportType);
    XLSX.writeFile(wb, `GSTR1_${reportType}_${fromDate}_to_${toDate}.xlsx`);
  }, [reportData, reportType, fromDate, toDate]);

  const activeTab  = reportTabs.find(t => t.id === reportType);
  const columns    = reportData.length > 0 ? Object.keys(reportData[0]) : [];

  // ✅ Numeric column detect karna
  const isNumericCol = (col) => {
    if (!reportData.length) return false;
    const val = reportData[0][col];
    return typeof val === "number";
  };

  return (
    <div style={{ padding: "0", fontSize: "12px" }}>
      <div style={{
        background: "white", borderRadius: "8px",
        boxShadow: "0 1px 10px rgba(0,0,0,0.06)",
        border: "1px solid #e0e6ed", overflow: "hidden"
      }}>

        {/* ── Header ── */}
        <div style={{
          background: activeTab?.color || "#0088cc",
          padding: "10px 16px",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          transition: "background 0.3s"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, color: "white" }}>
            <div style={{
              width: 34, height: 34, background: "rgba(255,255,255,0.2)",
              borderRadius: 6, display: "flex", alignItems: "center",
              justifyContent: "center", fontSize: 16
            }}>
              <i className={`bi ${activeTab?.icon || "bi-file-earmark"}`}></i>
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>GSTR-1 Export Report</div>
              <div style={{ fontSize: 11, opacity: 0.85 }}>
                {activeTab?.label} — Tax & Compliance Data
              </div>
            </div>
          </div>
          <button
            onClick={exportToExcel}
            disabled={!reportData.length}
            style={{
              padding: "5px 14px", fontSize: 11, fontWeight: 600,
              background: "white", border: "none", borderRadius: 4,
              cursor: reportData.length ? "pointer" : "not-allowed",
              opacity: reportData.length ? 1 : 0.5, color: "#1e293b"
            }}
          >
            <i className="bi bi-file-earmark-excel text-success me-1"></i>
            Export Excel
          </button>
        </div>

        {/* ── Tabs ── */}
        <div style={{
          display: "flex", background: "#f8fafc",
          borderBottom: "1px solid #e0e6ed",
          padding: "0 12px", gap: 4, overflowX: "auto"
        }}>
          {reportTabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setReportType(tab.id)}
              style={{
                padding: "9px 13px", border: "none", background: "transparent",
                color: reportType === tab.id ? tab.color : "#64748b",
                fontWeight: 600, fontSize: 12, cursor: "pointer",
                borderBottom: reportType === tab.id
                  ? `2px solid ${tab.color}` : "2px solid transparent",
                whiteSpace: "nowrap", transition: "color 0.2s"
              }}
            >
              <i className={`bi ${tab.icon} me-1`}></i>
              {tab.label}
            </button>
          ))}
        </div>

        <div style={{ padding: 14 }}>

          {/* ── Error ── */}
          {error && (
            <div style={{
              padding: "8px 12px", background: "#fee2e2", color: "#dc2626",
              borderRadius: 6, marginBottom: 10, fontSize: 12
            }}>
              ⚠️ {error}
            </div>
          )}

          {/* ── Filter Bar ── */}
          <div style={{
            display: "flex", gap: 8, background: "#f8fafc",
            padding: "10px 12px", borderRadius: 6,
            border: "1px solid #e0e6ed", marginBottom: 12,
            alignItems: "flex-end", flexWrap: "wrap"
          }}>
            {[
              { label: "From Date", val: fromDate, set: setFromDate },
              { label: "To Date",   val: toDate,   set: setToDate   },
            ].map(({ label, val, set }) => (
              <div key={label} style={{ display: "flex", flexDirection: "column" }}>
                <label style={{ fontSize: 11, fontWeight: 600, marginBottom: 3, color: "#475569" }}>
                  {label}
                </label>
                <input
                  type="date" value={val}
                  onChange={e => set(e.target.value)}
                  style={{
                    padding: "4px 8px", border: "1px solid #cbd5e1",
                    borderRadius: 4, fontSize: 12, height: 30, outline: "none"
                  }}
                />
              </div>
            ))}

            <button
              onClick={fetchReport} disabled={loading}
              style={{
                padding: "0 18px", height: 30,
                background: activeTab?.color || "#0088cc",
                color: "white", border: "none", borderRadius: 4,
                fontWeight: 600, cursor: loading ? "not-allowed" : "pointer",
                fontSize: 12, opacity: loading ? 0.7 : 1
              }}
            >
              {loading ? "Loading..." : "View Report"}
            </button>

            {/* ── Tab ke liye helpful hint ── */}
            <div style={{
              marginLeft: "auto", fontSize: 11, color: "#94a3b8",
              display: "flex", alignItems: "center", gap: 4
            }}>
              <i className="bi bi-info-circle"></i>
              {reportType === "B2B"         && "Registered customers (GSTIN wale) ki sales"}
              {reportType === "B2C"         && "Unregistered customers (bina GSTIN) ki sales — State & Rate wise"}
              {reportType === "HSN"         && "Item wise tax summary — Sirf SALE transactions"}
              {reportType === "CDNR"        && "Registered customers ke medicine returns (Credit Notes)"}
              {reportType === "B2CS_RETURN" && "Unregistered customers ke medicine returns"}
            </div>
          </div>

          {/* ── Summary Cards ── */}
          {!loading && reportData.length > 0 && (
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(165px, 1fr))",
              gap: 10, marginBottom: 14
            }}>
              {[
                {
                  label: "Taxable Value",
                  val: `₹ ${summary.taxable.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
                  bg: "linear-gradient(135deg,#3b82f6,#2563eb)"
                },
                {
                  label: "CGST + SGST",
                  val: `₹ ${(summary.cgst + summary.sgst).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
                  sub: `C: ₹${summary.cgst.toFixed(2)}  |  S: ₹${summary.sgst.toFixed(2)}`,
                  bg: "linear-gradient(135deg,#14b8a6,#047857)"
                },
                {
                  label: "IGST Amount",
                  val: `₹ ${summary.igst.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
                  bg: "linear-gradient(135deg,#f59e0b,#b45309)"
                },
                {
                  label: reportType === "CDNR" || reportType === "B2CS_RETURN"
                    ? "Total Note Value" : "Total Invoice Value",
                  val: `₹ ${summary.totalValue.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
                  sub: `${summary.billCount} ${
                    reportType === "B2B" || reportType === "CDNR" ? "Bills" : "Records"
                  }`,
                  bg: "linear-gradient(135deg,#8b5cf6,#6d28d9)"
                },
              ].map((card, i) => (
                <div key={i} style={{
                  padding: "12px 14px", borderRadius: 8, color: "white",
                  background: card.bg, boxShadow: "0 2px 6px rgba(0,0,0,0.12)"
                }}>
                  <div style={{ fontSize: 10, opacity: 0.9, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    {card.label}
                  </div>
                  <div style={{ fontSize: 17, fontWeight: 700, marginTop: 3 }}>{card.val}</div>
                  {card.sub && (
                    <div style={{ fontSize: 10, opacity: 0.75, marginTop: 4 }}>{card.sub}</div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* ── Data Table ── */}
          <div style={{
            border: "1px solid #e0e6ed", borderRadius: 6, overflow: "hidden"
          }}>
            <div style={{ overflowX: "auto", maxHeight: 480 }}>
              <table style={{
                width: "100%", borderCollapse: "collapse", fontSize: 12
              }}>
                <thead>
                  <tr>
                    <th style={thStyle("#f8fafc", "center")}>#</th>
                    {columns.map(col => (
                      <th
                        key={col}
                        style={thStyle("#f8fafc", isNumericCol(col) ? "right" : "left")}
                      >
                        {col.replace(/_/g, " ")}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td
                        colSpan={columns.length + 1}
                        style={{ textAlign: "center", padding: 30, color: "#94a3b8" }}
                      >
                        <i className="bi bi-hourglass-split me-2"></i>
                        Fetching GSTR-1 Data...
                      </td>
                    </tr>
                  ) : reportData.length === 0 ? (
                    <tr>
                      <td
                        colSpan={columns.length + 1}
                        style={{ textAlign: "center", padding: 30, color: "#94a3b8" }}
                      >
                        <i className="bi bi-inbox me-2"></i>
                        No records found. Select date range and click View Report.
                      </td>
                    </tr>
                  ) : (
                    reportData.map((row, ri) => (
                      <tr
                        key={ri}
                        style={{ background: ri % 2 === 0 ? "white" : "#fafafa" }}
                        onMouseEnter={e => e.currentTarget.style.background = "#f0f9ff"}
                        onMouseLeave={e => e.currentTarget.style.background = ri % 2 === 0 ? "white" : "#fafafa"}
                      >
                        <td style={tdStyle("center", true)}>{ri + 1}</td>
                        {columns.map(col => {
                          const val     = row[col];
                          const isNum   = typeof val === "number";
                          const isBill  = col === "Invoice_Number" || col === "Note_Number";
                          return (
                            <td
                              key={col}
                              style={{
                                ...tdStyle(isNum ? "right" : "left", false),
                                fontWeight: isNum ? 600 : 400,
                                color: isBill ? "#2563eb" : (isNum ? "#1e293b" : "#374151"),
                              }}
                            >
                              {isNum
                                ? val.toLocaleString("en-IN", { minimumFractionDigits: 2 })
                                : (val || "—")
                              }
                            </td>
                          );
                        })}
                      </tr>
                    ))
                  )}
                </tbody>

                {/* ── Column Totals Footer ── */}
                {reportData.length > 0 && (
                  <tfoot>
                    <tr style={{ background: "#1e293b", color: "white" }}>
                      <td style={{ padding: "7px 10px", fontWeight: 700, textAlign: "center" }}>
                        Σ
                      </td>
                      {columns.map(col => {
                        const isNum = isNumericCol(col);
                        const total = isNum
                          ? reportData.reduce((s, r) => s + Number(r[col] || 0), 0)
                          : null;
                        return (
                          <td
                            key={col}
                            style={{
                              padding: "7px 10px",
                              textAlign: isNum ? "right" : "left",
                              fontWeight: 700, fontSize: 12
                            }}
                          >
                            {isNum
                              ? total.toLocaleString("en-IN", { minimumFractionDigits: 2 })
                              : ""
                            }
                          </td>
                        );
                      })}
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>

          {/* ── Record Count ── */}
          {reportData.length > 0 && (
            <div style={{
              marginTop: 8, fontSize: 11, color: "#64748b", textAlign: "right"
            }}>
              Total {reportData.length} records
              {(reportType === "B2B" || reportType === "CDNR") &&
                ` across ${summary.billCount} bills`
              }
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

// ── Style helpers ──
const thStyle = (bg, align) => ({
  background: bg, color: "#334155",
  padding: "8px 10px", fontWeight: 600,
  borderBottom: "2px solid #e2e8f0",
  textAlign: align, whiteSpace: "nowrap",
  position: "sticky", top: 0, zIndex: 1,
  fontSize: 12
});

const tdStyle = (align, muted) => ({
  padding: "6px 10px",
  borderBottom: "1px solid #f1f5f9",
  textAlign: align,
  color: muted ? "#64748b" : "#1e293b",
  verticalAlign: "middle"
});

export default GSTR1Report;